import * as React from 'react';
import { Router } from 'react-router';
import { MasterContainer } from './containers/master';
import { HomePage } from './containers/home';
import { MusicArtistPage } from './containers/music-artist';
import { CheckoutContainer } from './containers/checkout';
import {clientOnly, maybeSkip, onEnterNoop, passThrough, requireAuth} from './lib/ssr';
import { PricingContainer } from './containers/pricing';
import { AccountPage } from './containers/account';
import { AccountLicencesPage } from './containers/account-licences';
import { AccountOrdersPage } from './containers/account-orders';
import { AccountOrderPage } from './containers/account-order';
import { SearchPage } from './containers/search';
import { AccountChannelsPage } from './containers/account-channels';
import { AccountChannelPage } from './containers/account-channel';
import { AccountBillingPage } from './containers/account-billing';
import { TermsAndConditionsPage } from './containers/terms-and-condtions';
import { ContactPage } from './containers/contact';
import { PrivacyPolicyPage } from './containers/privacy-policy';
import { FAQPage } from './containers/faq';
import { AboutPage } from './containers/about';
import { AccountCommunicationPage } from './containers/account-communication';
import { LicensingPage } from './containers/licensing';
import { SwapoutsPage } from './containers/swapouts';
import { AccountInvitesPage } from './containers/account-invites';
import { _404Page } from './containers/404';
import { CookiePolicyPage } from './containers/cookie-policy';
import { ArtistsPage } from './containers/artists';
import { AccountAttributionPage } from './containers/account-attribution';
import { AccountFavouritesPage } from './containers/account-favourites';
import { FeaturesPage } from './containers/features';
import { TrackPage } from './containers/track';
import { AccountProfilePage } from './containers/account-profile';
import { DiscoverPage } from './containers/discover';
import { YouTubePage } from './containers/youtube';
import { ClientModel } from './modules/client/model';
import { ClientController } from './modules/client/controller';
import {MusicSubmissionsPage} from './containers/music-submissions';
import {LearnPage} from './containers/learn';
import {BrowsePage} from './containers/browse';
import {EditorPage} from './containers/editor';
import {CareersPage} from './containers/careers';
import {SigninPage} from './containers/signin';
import GoldenTicketPage from './containers/golden-ticket';
import {ChannelReferralPage} from './containers/channel-referral';
import {AccountCouponsPage} from './containers/account-coupons';
import {JAACKMAATE_URL, SIGNIN_URL, VIDSUMMIT_URL, BLOGOSPHERE_URL, VIDCON_URL, FULLSCREEN_URL, TUBEBUDDY_URL} from './constants';
import LandingV2Page from './containers/landing-v2';
import {CampaignPage} from './containers/campaign';
import {FullscreenPage} from "./containers/fullscreen";
import {TubebuddyPage} from "./containers/tubebuddy";

export interface RouterSpec {
  path?: string;
  component: any;
  onEnter?: Router.EnterHook;
  onLeave?: any;
  onChange?: Router.ChangeHook;
  indexRoute?: ChildRouterSpec;
  childRoutes?: Array<ChildRouterSpec>;
}

export interface ChildRouterSpec extends RouterSpec {
  onEnter: Router.EnterHook;
  onLeave?: any;
  onChange?: Router.ChangeHook;
}

/*
 * NOTE: Always put more specific routes above less specific, so SSR route matching will work
 * e.g. /music/artists/:artist/release/:release goes before /music/artists/:artist
 */
export const createRoutes = (controller: ClientController, model: ClientModel): RouterSpec[] => {
  const { page, flash } = controller;
  const { auth } = model;

  return [
    {
      path: FULLSCREEN_URL,
      component: FullscreenPage
    },
    {
      path: TUBEBUDDY_URL,
      component: TubebuddyPage
    },
    {
      path: '/',
      component: MasterContainer,
      onEnter: page.master.onEnter,
      onLeave: page.master.onLeave,
      onChange: page.master.onLeave,
      indexRoute: {
        component: HomePage,
        onEnter: maybeSkip(page.home.onEnter),
      },
      childRoutes: [
        {
          path: SIGNIN_URL,
          component: SigninPage,
          onEnter: onEnterNoop
        },
        {
          path: JAACKMAATE_URL,
          component: CampaignPage,
          onEnter: passThrough(page.signin.onEnter)
        },
        {
          path: VIDSUMMIT_URL,
          component: CampaignPage,
          onEnter: passThrough(page.signin.onEnter)
        },
        {
          path: BLOGOSPHERE_URL,
          component: CampaignPage,
          onEnter: passThrough(page.signin.onEnter)
        },
        {
          path: VIDCON_URL,
          component: CampaignPage,
          onEnter: passThrough(page.signin.onEnter)
        },
        {
          path: '/search',
          component: SearchPage,
          onEnter: passThrough(page.search.onEnter),
          onLeave: page.search.onLeave
        },
        {
          path: '/pricing',
          component: PricingContainer,
          onEnter: maybeSkip(page.pricing.onEnter)
        },
        {
          path: '/account/favourites',
          component: AccountFavouritesPage,
          onEnter: requireAuth(page.favourites.onEnter, auth, flash)
        },
        {
          path: '/account/connected-accounts',
          component: AccountPage,
          onEnter: requireAuth(page.account.onEnter, auth, flash)
        },
        {
          path: '/account/licenses(/:page)',
          component: AccountLicencesPage,
          onEnter: requireAuth(page.accountLicences.onEnter, auth, flash)
        },
        {
          path: '/account/license/:identity/attribution',
          component: AccountAttributionPage,
          onEnter: requireAuth(page.accountAttribution.onEnter, auth, flash)
        },
        {
          path: '/account/orders(/:page)',
          component: AccountOrdersPage,
          onEnter: requireAuth(page.accountOrders.onEnter, auth, flash)
        },
        {
          path: '/account/order/:identity',
          component: AccountOrderPage,
          onEnter: requireAuth(page.accountOrder.onEnter, auth, flash)
        },
        {
          path: '/account/channels',
          component: AccountChannelsPage,
          onEnter: requireAuth(page.accountChannels.onEnter, auth, flash)
        },
        {
          path: '/account/channels/:identity',
          component: AccountChannelPage,
          onEnter: requireAuth(page.accountChannel.onEnter, auth, flash)
        },
        {
          path: '/account/billing',
          component: AccountBillingPage,
          onEnter: requireAuth(page.accountBilling.onEnter, auth, flash)
        },
        {
          path: '/account/communication',
          component: AccountCommunicationPage,
          onEnter: requireAuth(page.accountCommunication.onEnter, auth, flash)
        },
        {
          path: '/account/profile',
          component: AccountProfilePage,
          onEnter: requireAuth(page.accountProfile.onEnter, auth, flash)
        },
        {
          path: '/account/coupons',
          component: AccountCouponsPage,
          onEnter: requireAuth(page.accountCoupons.onEnter, auth, flash)
        },
        {
          path: '/account/invites',
          component: AccountInvitesPage,
          onEnter: requireAuth(page.accountInvites.onEnter, auth, flash)
        },
        {
          path: '/account',
          component: AccountInvitesPage,
          onEnter: requireAuth(page.accountInvites.onEnter, auth, flash)
        },
        {
          path: '/checkout(/:step)',
          component: CheckoutContainer,
          onEnter: clientOnly(page.checkout.onEnter),
          onLeave: page.checkout.onLeave
        },
        {
          path: '/browse/artists/:letter',
          component: ArtistsPage,
          onEnter: maybeSkip(page.artists.onEnter)
        },
        {
          path: '/browse/artists',
          component: ArtistsPage,
          onEnter: maybeSkip(page.artists.onEnter)
        },
        {
          path: '/browse/:type/:slug(/:page)',
          component: BrowsePage,
          onEnter: maybeSkip(page.browse.onEnterTag)
        },
        {
          path: '/browse/:type',
          component: BrowsePage,
          onEnter: maybeSkip(page.browse.onEnter)
        },
        {
          path: '/browse',
          component: BrowsePage,
          onEnter: maybeSkip(page.browse.onEnter)
        },
        {
          path: '/editor',
          component: EditorPage,
          onEnter: maybeSkip(page.browse.onEnter)
        },
        {
          path: '/about',
          component: AboutPage,
          onEnter: maybeSkip(page.about.onEnter)
        },
        {
          path: '/contact',
          component: ContactPage,
          onEnter: maybeSkip(page.contact.onEnter)
        },
        {
          path: '/music/artists/:artist/release/:release',
          component: MusicArtistPage,
          onEnter: maybeSkip(page.artist.onEnterRelease)
        },
        {
          path: '/music/artists/:artist/track/:track',
          component: TrackPage,
          onEnter: maybeSkip(page.track.onEnter)
        },
        {
          path: '/music/artists/:artist',
          component: MusicArtistPage,
          onEnter: maybeSkip(page.artist.onEnter)
        },
        {
          path: '/faq',
          component: FAQPage,
          onEnter: maybeSkip(page.faq.onEnter)
        },
        {
          path: '/privacy-policy',
          component: PrivacyPolicyPage,
          onEnter: maybeSkip(page.privacyPolicy.onEnter)
        },
        {
          path: '/cookie-policy',
          component: CookiePolicyPage,
          onEnter: maybeSkip(page.cookiePolicy.onEnter)
        },
        {
          path: '/terms-and-conditions',
          component: TermsAndConditionsPage,
          onEnter: maybeSkip(page.termsAndConditions.onEnter)
        },
        {
          path: '/music-submissions',
          component: MusicSubmissionsPage,
          onEnter: maybeSkip(page.musicSubmissions.onEnter)
        },
        {
          path: '/licensing',
          component: LicensingPage,
          onEnter: maybeSkip(page.licensing.onEnter)
        },
        {
          path: '/swapouts',
          component: SwapoutsPage,
          onEnter: maybeSkip(page.swapouts.onEnter)
        },
        {
          path: '/pricing',
          component: PricingContainer,
          onEnter: maybeSkip(page.pricing.onEnter)
        },
        {
          path: '/es/features',
          component: FeaturesPage,
          onEnter: maybeSkip(page.features.onEnter)
        },
        {
          path: '/features',
          component: FeaturesPage,
          onEnter: maybeSkip(page.features.onEnter)
        },
        {
          path: '/discover/:slug',
          component: DiscoverPage,
          onEnter: maybeSkip(page.discover.onEnter)
        },
        {
          path: '/learn(/:slug)',
          component: LearnPage,
          onEnter: maybeSkip(page.learn.onEnter)
        },
        {
          path: '/careers(/:slug)',
          component: CareersPage,
          onEnter: maybeSkip(page.careers.onEnter)
        },
        {
          path: '/youtube',
          component: YouTubePage,
          onEnter: maybeSkip(page.youtube.onEnter)
        },
        {
          path: '/golden-ticket',
          component: GoldenTicketPage,
          onEnter: maybeSkip(page.goldenTicket.onEnter)
        },
        {
          path: '/r(/:channel)',
          component: ChannelReferralPage,
          onEnter: maybeSkip(page.channelReferral.onEnter)
        },
        {
          path: '/l(/:slug)',
          component: LandingV2Page,
          onEnter: maybeSkip(page.landing.onEnter)
        },
        {
          path: '/**',
          component: _404Page,
          onEnter: passThrough(() => true)
        }
      ]
    }
  ]
};